import { frontendURL } from '../../../../helper/URLHelper';
const BillingSettingsLayout = () => import('./BillingSettingsLayout.vue');
const WhatsappFees = () => import('../account/WhatsappFees.vue');
const OrganizationBilling = () => import('../account/OrganizationBilling.vue');
const OrganizationBillingSelectPlan = () =>
  import('../account/OrganizationBillingSelectPlan.vue');
const AiCredits = () => import('../account/AiCredits.vue');
const OrganizationInfo = () => import('../account/OrganizationInfo.vue');
const OrganizationWorkspaces = () =>
  import('../account/OrganizationWorkspaces.vue');
const OrganizationSecurity = () => import('../security/Index.vue');
import { INSTALLATION_TYPES } from 'dashboard/constants/installationTypes';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import store from 'dashboard/store';
import { RouterView } from 'vue-router';

const organizationAccount = organizationId =>
  store.getters.getUserAccounts?.find(
    account => account.organization_id === Number(organizationId)
  );

const canManageOrganization = account => {
  if (account?.organization_id) {
    return ['owner', 'admin'].includes(account.organization_role);
  }

  return ['owner', 'administrator'].includes(account?.role);
};

const requireOrganizationManager = to => {
  const account = to.params.organizationId
    ? organizationAccount(to.params.organizationId)
    : store.getters.getUserAccounts?.find(
        item => item.id === Number(to.params.accountId)
      );

  if (canManageOrganization(account)) return true;

  return account
    ? { name: 'home', params: { accountId: account.id } }
    : { name: 'no_accounts' };
};

const billingChildren = ({ legacy = false } = {}) => [
  {
    path: '',
    name: legacy
      ? 'legacy_general_settings_billing'
      : 'general_settings_billing',
    component: OrganizationBilling,
    meta: {
      permissions: legacy
        ? ['administrator', 'owner']
        : ['administrator', 'agent', 'custom_role'],
    },
  },
  {
    path: 'usage',
    name: legacy
      ? 'legacy_general_settings_billing_usage'
      : 'general_settings_billing_usage',
    redirect: to => {
      const { tab, ...query } = to.query;
      const routeName =
        tab === 'ai'
          ? 'general_settings_ai_credits'
          : 'general_settings_billing';
      return {
        name: legacy ? `legacy_${routeName}` : routeName,
        params: to.params,
        query: tab === 'ai' ? { ...query, tab: 'analytics' } : query,
        hash: tab === 'active_contacts' ? '#active-contacts' : to.hash,
      };
    },
    meta: {
      permissions: legacy
        ? ['administrator', 'owner']
        : ['administrator', 'agent', 'custom_role'],
    },
  },
  {
    path: 'plans',
    name: legacy
      ? 'legacy_general_settings_billing_plans'
      : 'general_settings_billing_plans',
    component: OrganizationBillingSelectPlan,
    meta: {
      permissions: legacy
        ? ['administrator', 'owner']
        : ['administrator', 'agent', 'custom_role'],
    },
  },
  {
    path: 'whatsapp-fees',
    name: legacy
      ? 'legacy_general_settings_whatsapp_fees'
      : 'general_settings_whatsapp_fees',
    component: WhatsappFees,
    meta: {
      permissions: legacy
        ? ['administrator', 'owner']
        : ['administrator', 'agent', 'custom_role'],
    },
  },
  {
    path: 'ai-credits',
    name: legacy
      ? 'legacy_general_settings_ai_credits'
      : 'general_settings_ai_credits',
    component: AiCredits,
    meta: {
      permissions: legacy
        ? ['administrator', 'owner']
        : ['administrator', 'agent', 'custom_role'],
    },
  },
  {
    path: 'ai-billing',
    name: legacy
      ? 'legacy_general_settings_ai_billing'
      : 'general_settings_ai_billing',
    redirect: to => ({
      name: legacy
        ? 'legacy_general_settings_ai_credits'
        : 'general_settings_ai_credits',
      params: to.params,
      query: { ...to.query, tab: 'analytics' },
    }),
  },
];

export default {
  organizationRoutes: [
    {
      path: '',
      meta: {
        permissions: ['administrator', 'agent', 'custom_role'],
        fullWidth: true,
      },
      component: BillingSettingsLayout,
      beforeEnter: requireOrganizationManager,
      children: [
        {
          path: '',
          redirect: to => ({
            name: 'organization_settings_info',
            params: { organizationId: to.params.organizationId },
          }),
        },
        {
          path: 'settings/general-info',
          name: 'organization_settings_info',
          component: OrganizationInfo,
          meta: {
            permissions: ['administrator', 'agent', 'custom_role'],
          },
        },
        {
          path: 'settings/workspaces',
          name: 'organization_settings_workspaces',
          component: OrganizationWorkspaces,
          meta: {
            permissions: ['administrator', 'agent', 'custom_role'],
          },
        },
        {
          path: 'settings/security',
          name: 'organization_settings_security',
          component: OrganizationSecurity,
          meta: {
            permissions: ['administrator', 'agent', 'custom_role'],
            featureFlag: FEATURE_FLAGS.SAML,
            installationTypes: [
              INSTALLATION_TYPES.CLOUD,
              INSTALLATION_TYPES.ENTERPRISE,
            ],
          },
        },
        {
          path: 'billing',
          component: RouterView,
          children: billingChildren(),
        },
      ],
    },
  ],
  routes: [
    {
      path: frontendURL('accounts/:accountId/settings/organization'),
      component: BillingSettingsLayout,
      beforeEnter: requireOrganizationManager,
      children: [
        {
          path: '',
          name: 'legacy_organization_settings_info',
          component: OrganizationInfo,
          meta: {
            permissions: ['administrator', 'owner'],
          },
        },
        {
          path: 'workspaces',
          name: 'legacy_organization_settings_workspaces',
          component: OrganizationWorkspaces,
          meta: {
            permissions: ['administrator', 'owner'],
          },
        },
        {
          path: 'security',
          name: 'legacy_organization_settings_security',
          component: OrganizationSecurity,
          meta: {
            permissions: ['administrator', 'owner'],
            featureFlag: FEATURE_FLAGS.SAML,
            installationTypes: [
              INSTALLATION_TYPES.CLOUD,
              INSTALLATION_TYPES.ENTERPRISE,
            ],
          },
        },
      ],
    },
    {
      path: frontendURL('accounts/:accountId/billing'),
      component: BillingSettingsLayout,
      beforeEnter: requireOrganizationManager,
      children: billingChildren({ legacy: true }),
    },
  ],
};
